import { coerceNumberProperty } from '@angular/cdk/coercion';
import { Injectable } from '@angular/core';
import { SortDirection } from '@angular/material/sort';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { UIService } from '@services';
import {
  defaultPageSize,
  getGatewayMainDevices,
  InstallationFeature,
  InstallationsFilterType,
  InstallationsStore,
  OperatingModesStore,
} from '@store';

import {
  AggregatedStatus,
  InstallationItem,
  InstallationItemGateway,
  InstallationItemGatewayDeviceFull,
} from '@vi-iot/client';
import { includes, isEmpty, last } from 'lodash-es';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';

export interface Sorter {
  name: string;
  label: string;
  flex: string;
  enabled: boolean;
}

export interface Param {
  pageIndex: number;
  pageSize: number;
  sortBy: string;
  orderBy: 'asc' | 'desc' | '';
  filter: InstallationsFilterType;
  search: string;
}

@Injectable({
  providedIn: 'root',
})
export class InstallationsService {
  locale;
  // Paging stream
  installations$: BehaviorSubject<InstallationItem[]> = new BehaviorSubject<InstallationItem[]>([]);

  heatingModes: { [key: string]: InstallationFeature<{ value: string }>[] } = {};

  sortedInstallations: InstallationItem[] = [];
  sourcedInstallations: InstallationItem[] = [];
  processedInstallations: InstallationItem[] = [];

  pageSizes: number[] = [5, 10, 25];

  selectedInstallationId: number = null;
  errorsAmount: number = null;
  offlineAmount: number = null;
  maintenanceAmount: number = null;
  remoteDiagnosticAmount: number = null;
  coldSystemAmount: number = null;
  workingAmount: number = null;
  isSearchingLimited: boolean;
  isSearchInfoBox$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  sorters: Sorter[] = [
    {
      name: 'marketingName',
      label: '+INSTALLATIONS.INSTALLATIONS_SERVICE.SORTER.DEVICE_TYPE',
      flex: '520px',
      enabled: true,
    },
    {
      name: 'serial',
      label: '+INSTALLATIONS.INSTALLATION.DEVICE_SERIAL_NUMBER',
      flex: '150px',
      enabled: false,
    },
    {
      name: 'ownerName',
      label: '+INSTALLATIONS.INSTALLATIONS_SERVICE.SORTER.SYSTEM_OWNER',
      flex: '180px',
      enabled: true,
    },
    {
      name: 'location',
      label: '+INSTALLATIONS.INSTALLATIONS_SERVICE.SORTER.LOCATION',
      flex: '270px',
      enabled: true,
    },
    {
      name: 'remoteInspection',
      label: '+INSTALLATIONS.INSTALLATIONS_SERVICE.SORTER.REMOTE_INSPECTION',
      flex: '200px',
      enabled: false,
    },
    {
      name: 'package',
      label: '',
      flex: '40px',
      enabled: true,
    },
  ];

  ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private operatingModesStore: OperatingModesStore,
    private installationsStore: InstallationsStore,
    private translation: TranslateService,
    private uiService: UIService,
    private router: Router
  ) {
    this.processInstallationGateways();
  }

  listInstallations(): void {
    this.installationsStore.listInstallations();
  }

  getInstallationOperatingModes(id: number, serial: string, deviceId: string): void {
    this.operatingModesStore.getOperatingModes(id, serial, deviceId);
  }

  removeFromSource(id: number): void {
    this.installationsStore.removeInstallation(id);
  }

  pageInstallations(
    pageIndex: number,
    pageSize: number,
    isSource: boolean,
    activeFilter: InstallationsFilterType,
    search: string
  ): void {
    activeFilter = this.coerceFilterParam(activeFilter);
    this.filterInstallations(activeFilter);

    search = this.coerceSearchParam(search);
    if (search) {
      this.processedInstallations = this.searchInstallations(search, this.processedInstallations);
    }
    if (isSource || isEmpty(this.sortedInstallations)) {
      this.installations$.next(
        this.processedInstallations.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize)
      );
    } else {
      this.installations$.next(
        this.sortedInstallations.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize)
      );
    }
  }

  sortInstallations(param: Param): void {
    if (!param.sortBy || !param.orderBy) {
      this.pageInstallations(param.pageIndex, param.pageSize, true, param.filter, param.search);
      this.sortedInstallations = [];

      return;
    }

    const isAsc = param.orderBy === 'asc';

    if (param.sortBy === 'marketingName') {
      this.sortByMarketingName(isAsc);
    } else {
      this.sortedInstallations = this.processedInstallations.slice().sort((a, b) => {
        switch (param.sortBy) {
          case 'ownerName':
            return this.compare(a.ownerName, b.ownerName, isAsc);
          case 'location':
            return this.compare(a.location, b.location, isAsc);
          default:
            return 0;
        }
      });
    }

    this.pageInstallations(0, param.pageSize, false, param.filter, param.search);
  }

  filterInstallations(activeFilter: InstallationsFilterType): void {
    if (activeFilter === InstallationsFilterType.Error) {
      this.processedInstallations = this.sourcedInstallations
        .slice()
        .filter(
          (installation) =>
            !installation.isColdSystem &&
            installation.gateways.some(
              (gateway) => gateway.aggregatedStatus === AggregatedStatus.Error
            )
        );
    } else if (activeFilter === InstallationsFilterType.Offline) {
      this.processedInstallations = this.sourcedInstallations
        .slice()
        .filter(
          (installation) =>
            !installation.isColdSystem &&
            installation.gateways.some(
              (gateway) => gateway.aggregatedStatus === AggregatedStatus.Offline
            )
        )
        .sort(
          (a, b) =>
            new Date(a.gateways[0].lastStatusChangedAt).getTime() -
            new Date(b.gateways[0].lastStatusChangedAt).getTime()
        );
    } else if (activeFilter === InstallationsFilterType.Maintenance) {
      this.processedInstallations = this.sourcedInstallations
        .slice()
        .filter(
          (installation) =>
            !installation.isColdSystem &&
            installation.gateways.some(
              (gateway) => gateway.aggregatedStatus === AggregatedStatus.Maintenance
            )
        );
    } else if (activeFilter === InstallationsFilterType.RemoteDiagnostic) {
      this.processedInstallations = this.sourcedInstallations
        .slice()
        .filter(
          (installation) =>
            !installation.isColdSystem &&
            installation.gateways.some(
              (gateway) => gateway.aggregatedStatus === AggregatedStatus.RemoteDiagnosticSession
            )
        );
    } else if (activeFilter === InstallationsFilterType.Working) {
      this.processedInstallations = this.sourcedInstallations
        .slice()
        .filter(
          (installation) =>
            !installation.isColdSystem &&
            installation.gateways.some(
              (gateway) => gateway.aggregatedStatus === AggregatedStatus.WorksProperly
            )
        );
    } else if (activeFilter === InstallationsFilterType.ColdSystem) {
      this.processedInstallations = this.sourcedInstallations
        .slice()
        .filter((installation) => installation.isColdSystem);
    } else {
      this.processedInstallations = this.sourcedInstallations.slice();
    }
  }

  searchInstallations(
    userInputString: string,
    installations: InstallationItem[]
  ): InstallationItem[] {
    if (userInputString) {
      const textLowercase = userInputString.toLowerCase();

      return installations.filter(
        (installation) =>
          installation.ownerName.toLowerCase().includes(textLowercase) ||
          installation.location.toLowerCase().includes(textLowercase) ||
          installation.installationType.toLowerCase().includes(textLowercase) ||
          installation.gateways.some(
            (gateway) =>
              gateway.serial.includes(textLowercase) ||
              gateway.devices?.some(
                (device) =>
                  device.id === '0' &&
                  (device.serial?.includes(textLowercase) ||
                    device.marketingName?.toLowerCase()?.includes(textLowercase))
              )
          )
      );
    } else {
      return [];
    }
  }

  searchDevices(
    userInputString: string,
    installations: InstallationItem[]
  ): InstallationItemGatewayDeviceFull[] {
    if (userInputString) {
      const textLowercase = userInputString.toLowerCase();
      const devicesSet = new Set<InstallationItemGatewayDeviceFull>();

      installations.forEach((installation) => {
        installation.gateways.forEach((gateway) =>
          gateway.devices.forEach((device) => {
            if (
              installation.ownerName.toLowerCase().includes(textLowercase) ||
              installation.location.toLowerCase().includes(textLowercase) ||
              device.gatewaySerial?.includes(textLowercase) ||
              device.serial?.includes(textLowercase) ||
              device.marketingName?.toLowerCase()?.includes(textLowercase)
            ) {
              devicesSet.add({
                ...device,
                installationId: installation.id,
                ownerName: installation.ownerName,
              });
            }
          })
        );
      });

      return Array.from(devicesSet);
    } else {
      return [];
    }
  }

  isMainDevice(deviceId: string): boolean {
    return deviceId === '0';
  }

  coercePageParam(value: string): number {
    const page = Math.abs(coerceNumberProperty(value));

    return page ? page - 1 : 0;
  }

  coerceSizeParam(value: string): any {
    const pageSize = Math.abs(coerceNumberProperty(value)) || defaultPageSize;

    if (!includes(this.pageSizes, pageSize)) {
      this.uiService.installationsQueryParams.size =
        pageSize > last(this.pageSizes) ? last(this.pageSizes) : defaultPageSize;

      this.router.navigate(['/installations'], {
        queryParams: this.uiService.installationsQueryParams,
      });
    }

    return pageSize;
  }

  coerceSortParam(value: string): string {
    const sort: Sorter = this.sorters.find((s) => s.name === value);

    return sort ? sort.name : undefined;
  }

  coerceOrderParam(value: string): SortDirection {
    return value === 'asc' ? 'asc' : value === 'desc' ? 'desc' : '';
  }

  coerceFilterParam(value: InstallationsFilterType): InstallationsFilterType {
    if (value === InstallationsFilterType.Error) {
      return this.errorsAmount ? InstallationsFilterType.Error : undefined;
    } else if (value === InstallationsFilterType.Maintenance) {
      return this.maintenanceAmount ? InstallationsFilterType.Maintenance : undefined;
    } else if (value === InstallationsFilterType.Offline) {
      return this.offlineAmount ? InstallationsFilterType.Offline : undefined;
    } else if (value === InstallationsFilterType.RemoteDiagnostic) {
      return this.remoteDiagnosticAmount ? InstallationsFilterType.RemoteDiagnostic : undefined;
    } else if (value === InstallationsFilterType.ColdSystem) {
      return this.coldSystemAmount ? InstallationsFilterType.ColdSystem : undefined;
    } else if (value === InstallationsFilterType.Working) {
      return this.workingAmount ? InstallationsFilterType.Working : undefined;
    } else {
      return InstallationsFilterType.All;
    }
  }

  coerceSearchParam(value: string): string | undefined {
    return value && value.trim() ? value.trim() : undefined;
  }

  resetSubscriptions(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.ngUnsubscribe = new Subject<void>();
  }

  getBoilerSerialOfGatewayMainDevice(gateways: InstallationItemGateway[]): string {
    return getGatewayMainDevices(gateways)[0]?.serial;
  }

  private processInstallationGateways(): void {
    this.installationsStore.state$
      .pipe(
        filter((state) => state.isData),
        map((state) => state)
      )
      .subscribe((state) => {
        let lockedInstallationsWithWorkingStatusAmount = 0;
        let lockedInstallationsWithErrorStatusAmount = 0;
        let lockedInstallationsWithOfflineStatusAmount = 0;
        let lockedInstallationsWithMaintenanceStatusAmount = 0;
        let lockedInstallationsWithRemoteDiagnosticStatusAmount = 0;
        const installations = state.installations;
        const lockedInstallations = state.lockedInstallations;
        const noBoilerSerialOrDeviceType = installations.some((installation) =>
          installation.gateways.some((gateway) =>
            gateway.devices.some((device) => !!device.serial || !!device.deviceType)
          )
        );
        const installationsWithWorkingStatusAmount = installations.filter((installation) =>
          installation.gateways.some(
            (gateway) => gateway?.aggregatedStatus === AggregatedStatus.WorksProperly
          )
        ).length;
        const installationsWithErrorStatusAmount = installations.filter((installation) =>
          installation.gateways.some(
            (gateway) => gateway?.aggregatedStatus === AggregatedStatus.Error
          )
        ).length;
        const installationsWithOfflineStatusAmount = installations.filter(
          (installation) =>
            installation.gateways.some(
              (gateway) => gateway?.aggregatedStatus === AggregatedStatus.Offline
            ) || installation.gateways.length === 0
        ).length;
        const installationsWithMaintenanceStatusAmount = installations.filter((installation) =>
          installation.gateways.some(
            (gateway) => gateway?.aggregatedStatus === AggregatedStatus.Maintenance
          )
        ).length;
        const installationsWithRemoteDiagnosticSessionStatusAmount = installations.filter(
          (installation) =>
            installation.gateways.some(
              (gateway) => gateway?.aggregatedStatus === AggregatedStatus.RemoteDiagnosticSession
            )
        ).length;

        if (lockedInstallations.length) {
          lockedInstallationsWithWorkingStatusAmount = lockedInstallations.filter((installation) =>
            installation?.gateways.some(
              (gateway) => gateway?.aggregatedStatus === AggregatedStatus.WorksProperly
            )
          ).length;
          lockedInstallationsWithErrorStatusAmount = lockedInstallations.filter((installation) =>
            installation?.gateways.some(
              (gateway) => gateway?.aggregatedStatus === AggregatedStatus.Error
            )
          ).length;
          lockedInstallationsWithOfflineStatusAmount = lockedInstallations.filter((installation) =>
            installation?.gateways.some(
              (gateway) => gateway?.aggregatedStatus === AggregatedStatus.Offline
            )
          ).length;
          lockedInstallationsWithMaintenanceStatusAmount = lockedInstallations.filter(
            (installation) =>
              installation?.gateways.some(
                (gateway) => gateway?.aggregatedStatus === AggregatedStatus.Maintenance
              )
          ).length;
          lockedInstallationsWithRemoteDiagnosticStatusAmount = lockedInstallations.filter(
            (installation) =>
              installation?.gateways.some(
                (gateway) => gateway?.aggregatedStatus === AggregatedStatus.RemoteDiagnosticSession
              )
          ).length;
        }

        this.workingAmount =
          installationsWithWorkingStatusAmount - lockedInstallationsWithWorkingStatusAmount;

        this.errorsAmount =
          installationsWithErrorStatusAmount - lockedInstallationsWithErrorStatusAmount;

        this.offlineAmount =
          installationsWithOfflineStatusAmount - lockedInstallationsWithOfflineStatusAmount;

        this.maintenanceAmount =
          installationsWithMaintenanceStatusAmount - lockedInstallationsWithMaintenanceStatusAmount;

        this.remoteDiagnosticAmount =
          installationsWithRemoteDiagnosticSessionStatusAmount -
          lockedInstallationsWithRemoteDiagnosticStatusAmount;

        this.coldSystemAmount = installations.filter((inst) => inst.isColdSystem).length;

        this.isSearchingLimited = !!noBoilerSerialOrDeviceType;
        this.sourcedInstallations = installations.slice();
      });
  }

  private compare(a: string, b: string, isAsc: boolean): number {
    const result = new Intl.Collator(this.translation.currentLang, { sensitivity: 'base' }).compare(
      a,
      b
    );

    return (result < 0 ? -1 : 1) * (isAsc ? 1 : -1);
  }

  private sortByMarketingName(isAsc: boolean): void {
    let installationsWithMainDeviceMaterialName: InstallationItem[] = [];
    let installationsWithOwnerName: InstallationItem[] = [];
    this.processedInstallations.forEach((installation) => {
      const mainDevices = getGatewayMainDevices(installation.gateways || []);
      if (mainDevices?.[0]?.marketingName) {
        installationsWithMainDeviceMaterialName.push(installation);
      } else {
        installationsWithOwnerName.push(installation);
      }
    });

    installationsWithMainDeviceMaterialName = installationsWithMainDeviceMaterialName.sort(
      (a, b) => {
        const aMarketingName = getGatewayMainDevices(a.gateways)[0].marketingName;
        const bMarketingName = getGatewayMainDevices(b.gateways)[0].marketingName;

        return this.compare(aMarketingName, bMarketingName, isAsc);
      }
    );

    installationsWithOwnerName = installationsWithOwnerName.sort((a, b) => {
      const aMarketingName = a.ownerName;
      const bMarketingName = b.ownerName;

      return this.compare(aMarketingName, bMarketingName, isAsc);
    });

    this.sortedInstallations = [
      ...installationsWithMainDeviceMaterialName,
      ...installationsWithOwnerName,
    ];
  }
}
